import { useUnsaved } from "@/lib/unsaved";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trophy, Save, Coins, Plus, X, Store, Dices, Gauge, Medal, MessageSquare, PartyPopper } from "lucide-react";
import api from "@/lib/api";
import { useGuild } from "./DashboardLayout";
import { Toggle, PageHead } from "@/components/kit";
import { ChannelSelect } from "@/components/ChannelSelect";
import { RoleSelect } from "@/components/RoleSelect";
import { FunCommandsTab } from "@/components/FunCommandsTab";

const field = "mt-1 w-full bg-black border border-zinc-800 rounded-md px-3 py-2 outline-none focus:border-volt text-sm";
const lbl = "text-xs uppercase tracking-wider text-zinc-500";
const levelFloor = (lvl) => 300 * Math.pow(lvl - 1, 2);

const TABS = [
  { key: "leveling", label: "Leveling", icon: Trophy },
  { key: "economy", label: "Economy", icon: Coins },
  { key: "leaderboard", label: "Leaderboard", icon: Medal },
  { key: "fun", label: "Fun", icon: PartyPopper },
];

export default function Engagement() {
  const { guildId, refreshKey } = useGuild();
  const [tab, setTab] = useState("leveling");
  const [lvl, setLvl] = useState(null);
  const [eco, setEco] = useState(null);
  const [roles, setRoles] = useState([]);
  const [board, setBoard] = useState([]);
  const [sortBy, setSortBy] = useState("xp");
  const [item, setItem] = useState({ name: "", description: "", price: 100, type: "item", role_name: "", emoji: "" });

  const loadBoard = (s = sortBy) => api.get(`/guilds/${guildId}/engagement/leaderboard?sort=${s}`).then((r) => setBoard(r.data)).catch(() => setBoard([]));

  useEffect(() => {
    api.get(`/guilds/${guildId}/modules/leveling`).then((r) => setLvl(r.data));
    api.get(`/guilds/${guildId}/modules/economy`).then((r) => setEco(r.data));
    api.get(`/guilds/${guildId}/discord/roles`).then((r) => setRoles(r.data.roles || [])).catch(() => setRoles([]));
    loadBoard();
    // eslint-disable-next-line
  }, [guildId, refreshKey]);

  const switchSort = (s) => { setSortBy(s); loadBoard(s); };

  return (
    <div>
      <PageHead title="Economy" desc="Leveling, economy and a unified leaderboard — one connected system." />
      <div className="flex gap-2 mb-6 border-b border-zinc-800">
        {TABS.map((t) => (
          <button key={t.key} data-testid={`engagement-tab-${t.key}`} onClick={() => t.key === "leaderboard" ? (setTab(t.key), loadBoard()) : setTab(t.key)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === t.key ? "border-volt text-volt" : "border-transparent text-zinc-400 hover:text-zinc-200"}`}>
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      {tab === "leveling" && <LevelingTab guildId={guildId} lvl={lvl} setLvl={setLvl} roles={roles} />}
      {tab === "economy" && <EconomyTab guildId={guildId} eco={eco} setEco={setEco} item={item} setItem={setItem} />}
      {tab === "fun" && <FunCommandsTab guildId={guildId} />}
      {tab === "leaderboard" && <LeaderboardTab board={board} sortBy={sortBy} switchSort={switchSort} sym={eco?.config?.currency_symbol || "🪙"} />}
    </div>
  );
}

/* ---------------- Leveling ---------------- */
function LevelingTab({ guildId, lvl, setLvl, roles }) {
  const save = useUnsaved(lvl, setLvl, () => saveRaw());
  if (!lvl) return <p className="text-zinc-500">Loading…</p>;
  const c = lvl.config;
  const set = (k, v) => setLvl({ ...lvl, config: { ...c, [k]: v } });
  const rewards = c.role_rewards || [];
  const setReward = (i, k, v) => set("role_rewards", rewards.map((r, idx) => idx === i ? { ...r, [k]: v } : r));
  const addReward = () => set("role_rewards", [...rewards, { level: 5, role: "" }]);
  const rmReward = (i) => set("role_rewards", rewards.filter((_, idx) => idx !== i));

  const saveRaw = async () => {
    await api.put(`/guilds/${guildId}/modules/leveling`, { enabled: lvl.enabled, config: c });
    toast.success("Leveling settings saved");
  };

  return (
    <div>
      <div className="flex items-center justify-end gap-4 mb-6">
        <div className="flex items-center gap-2 text-sm"><span className="text-zinc-400">Enabled</span><Toggle testid="leveling-enabled" checked={lvl.enabled} onChange={(v) => setLvl({ ...lvl, enabled: v })} /></div>
        <button data-testid="leveling-save" onClick={save} className="bg-volt text-volt-ink font-semibold rounded-md px-5 py-2 hover:bg-volt-hover flex items-center gap-2"><Save className="w-4 h-4" /> Save</button>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 space-y-5">
          <h3 className="font-head font-semibold flex items-center gap-2"><Gauge className="w-4 h-4 text-volt" /> XP & rewards</h3>
          <div className="grid grid-cols-2 gap-4">
            <div><label className={lbl}>XP per message</label>
              <input data-testid="xp-per-message" type="number" value={c.xp_per_message ?? 15} onChange={(e) => set("xp_per_message", +e.target.value)} className={field} /></div>
            <div><label className={lbl}>Cooldown (seconds)</label>
              <input type="number" value={c.cooldown ?? 60} onChange={(e) => set("cooldown", +e.target.value)} className={field} /></div>
          </div>
          <div>
            <label className={lbl}>Coins per level</label>
            <input data-testid="coins-per-level" type="number" value={c.coins_per_level ?? 100} onChange={(e) => set("coins_per_level", Math.max(0, +e.target.value))} className={field} />
            <p className="text-xs text-zinc-600 mt-1">On level-up, members earn <span className="text-volt">coins × new level</span> in the Economy (0 = off). Requires the Economy module enabled.</p>
          </div>
          <div><label className={lbl}>Level-up message</label>
            <textarea data-testid="level-up-message" value={c.level_up_message || ""} onChange={(e) => set("level_up_message", e.target.value)} rows={3} className={field} />
            <p className="text-xs text-zinc-600 mt-1">Variables: <code className="text-volt">{"{user}"}</code> <code className="text-volt">{"{level}"}</code> <code className="text-volt">{"{coins}"}</code> (coins earned).</p></div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className={lbl}>Announce channel</label>
              <ChannelSelect guildId={guildId} value={c.announce_channel} onChange={(v) => set("announce_channel", v)} testid="levelup-announce-channel" placeholder="— select a channel —" /></div>
            <div><label className={lbl}>Ping role on level-up</label>
              <select data-testid="levelup-ping-role" value={c.ping_role || ""} onChange={(e) => set("ping_role", e.target.value)} className={field}>
                <option value="">— no ping —</option>
                <option value="everyone">@everyone</option>
                <option value="here">@here</option>
                {roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select></div>
          </div>
        </div>

        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-head font-semibold flex items-center gap-2"><Trophy className="w-4 h-4 text-volt" /> Level role rewards</h3>
            <button data-testid="add-reward" onClick={addReward} className="text-sm bg-zinc-800 hover:bg-zinc-700 rounded-md px-3 py-1.5 flex items-center gap-1"><Plus className="w-3.5 h-3.5" /> Add</button>
          </div>
          <p className="text-xs text-zinc-600">Grant a role automatically when a member reaches a level. Enter the exact Discord role name.</p>
          <div className="space-y-2">
            {rewards.length === 0 && <p className="text-sm text-zinc-600">No role rewards yet.</p>}
            {rewards.map((r, i) => (
              <div key={i} data-testid={`reward-row-${i}`} className="flex items-center gap-3 bg-black border border-zinc-800 rounded-lg px-3 py-2">
                <div className="w-24"><label className="text-[10px] uppercase text-zinc-600">Level</label>
                  <input type="number" value={r.level} onChange={(e) => setReward(i, "level", +e.target.value)} className="w-full bg-zinc-900 border border-zinc-800 rounded px-2 py-1 text-sm outline-none focus:border-volt" /></div>
                <div className="flex-1"><label className="text-[10px] uppercase text-zinc-600">Role name</label>
                  <RoleSelect guildId={guildId} value={r.role} onChange={(v) => setReward(i, "role", v)} placeholder="— select a role —" className="w-full mt-0.5" /></div>
                <button onClick={() => rmReward(i)} className="text-zinc-500 hover:text-red-400 mt-4"><X className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Economy ---------------- */
function EconomyTab({ guildId, eco, setEco, item, setItem }) {
  const save = useUnsaved(eco, setEco, () => saveRaw());
  if (!eco) return <p className="text-zinc-500">Loading…</p>;
  const c = eco.config;
  const set = (k, v) => setEco((prev) => ({ ...prev, config: { ...prev.config, [k]: v } }));
  const num = (k, v) => set(k, Math.max(0, parseInt(v || 0, 10)));
  const shop = c.shop || [];
  const sym = c.currency_symbol || "🪙";

  const addItem = () => {
    if (!item.name.trim()) { toast.error("Give the item a name"); return; }
    if (item.type === "role" && !item.role_name.trim()) { toast.error("Enter the role name to grant"); return; }
    const id = (crypto.randomUUID ? crypto.randomUUID().slice(0, 6) : Math.random().toString(36).slice(2, 8));
    set("shop", [...shop, { ...item, id, price: Math.max(0, parseInt(item.price || 0, 10)) }]);
    setItem({ name: "", description: "", price: 100, type: "item", role_name: "", emoji: "" });
  };
  const removeItem = (id) => set("shop", shop.filter((i) => i.id !== id));
  const saveRaw = async () => {
    await api.put(`/guilds/${guildId}/modules/economy`, { enabled: eco.enabled, config: c });
    toast.success("Economy saved");
  };

  return (
    <div>
      <div className="flex items-center justify-end gap-4 mb-6">
        <div className="flex items-center gap-2 text-sm"><span className="text-zinc-400">Module</span><Toggle testid="economy-enabled" checked={eco.enabled} onChange={(v) => setEco({ ...eco, enabled: v })} /></div>
        <button data-testid="economy-save" onClick={save} className="bg-volt text-volt-ink font-semibold rounded-md px-5 py-2 hover:bg-volt-hover flex items-center gap-2"><Save className="w-4 h-4" /> Save</button>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 space-y-4">
          <h3 className="font-head font-semibold flex items-center gap-2"><Coins className="w-4 h-4 text-volt" /> Currency & rewards</h3>
          <div className="grid grid-cols-2 gap-4">
            <div><label className={lbl}>Currency name</label>
              <input data-testid="currency-name" value={c.currency_name || ""} onChange={(e) => set("currency_name", e.target.value)} className={field} placeholder="coins" /></div>
            <div><label className={lbl}>Symbol / emoji</label>
              <input data-testid="currency-symbol" value={c.currency_symbol || ""} onChange={(e) => set("currency_symbol", e.target.value)} className={field} placeholder="🪙" maxLength={8} /></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className={lbl}>Starting balance</label>
              <input data-testid="starting-balance" type="number" value={c.starting_balance ?? 100} onChange={(e) => num("starting_balance", e.target.value)} className={field} /></div>
            <div><label className={lbl}>Daily reward</label>
              <input data-testid="daily-amount" type="number" value={c.daily_amount ?? 250} onChange={(e) => num("daily_amount", e.target.value)} className={field} /></div>
          </div>
          <div><label className={lbl}>Daily streak bonus</label>
            <input data-testid="daily-streak-bonus" type="number" value={c.daily_streak_bonus ?? 50} onChange={(e) => num("daily_streak_bonus", e.target.value)} className={field} />
            <p className="text-xs text-zinc-600 mt-1">Added per consecutive day.</p></div>
        </div>

        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 space-y-4">
          <h3 className="font-head font-semibold flex items-center gap-2"><Dices className="w-4 h-4 text-volt" /> Casino minigames</h3>
          <div className="grid grid-cols-2 gap-4">
            <div><label className={lbl}>Min bet</label>
              <input data-testid="min-bet" type="number" value={c.min_bet ?? 10} onChange={(e) => num("min_bet", e.target.value)} className={field} /></div>
            <div><label className={lbl}>Max bet</label>
              <input data-testid="max-bet" type="number" value={c.max_bet ?? 10000} onChange={(e) => num("max_bet", e.target.value)} className={field} /></div>
          </div>
          <div className="space-y-3 pt-2">
            <div className="flex items-center justify-between"><span className="text-sm text-zinc-300">🪙 Coin Flip (/coinflip)</span><Toggle testid="coinflip-enabled" checked={c.coinflip_enabled !== false} onChange={(v) => set("coinflip_enabled", v)} /></div>
            <div className="flex items-center justify-between"><span className="text-sm text-zinc-300">🎰 Slots (/slots)</span><Toggle testid="slots-enabled" checked={c.slots_enabled !== false} onChange={(v) => set("slots_enabled", v)} /></div>
            <div className="flex items-center justify-between"><span className="text-sm text-zinc-300">🎡 Roulette (/roulette)</span><Toggle testid="roulette-enabled" checked={c.roulette_enabled !== false} onChange={(v) => set("roulette_enabled", v)} /></div>
          </div>
          <p className="text-xs text-zinc-600">Members also get <code className="text-volt">/balance /daily /give /deposit /withdraw /shop /buy /inventory /rich</code>.</p>
        </div>
      </div>

      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 mt-6">
        <h3 className="font-head font-semibold flex items-center gap-2 mb-4"><Coins className="w-4 h-4 text-volt" /> Bank interest & robbery</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div><label className={lbl}>Bank interest %/day</label>
            <input data-testid="bank-interest" type="number" value={c.bank_interest_rate ?? 2} onChange={(e) => num("bank_interest_rate", e.target.value)} className={field} /></div>
          <div><label className={lbl}>Rob success %</label>
            <input data-testid="rob-chance" type="number" value={c.rob_success_chance ?? 40} onChange={(e) => num("rob_success_chance", e.target.value)} className={field} /></div>
          <div><label className={lbl}>Rob cooldown (min)</label>
            <input data-testid="rob-cooldown" type="number" value={c.rob_cooldown_minutes ?? 60} onChange={(e) => num("rob_cooldown_minutes", e.target.value)} className={field} /></div>
          <div><label className={lbl}>Max steal % of wallet</label>
            <input data-testid="rob-max" type="number" value={c.rob_max_percent ?? 30} onChange={(e) => num("rob_max_percent", e.target.value)} className={field} /></div>
        </div>
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-zinc-800">
          <span className="text-sm text-zinc-300">🦹 Enable <code className="text-volt">/rob</code></span>
          <Toggle testid="rob-enabled" checked={c.rob_enabled !== false} onChange={(v) => set("rob_enabled", v)} />
        </div>
      </div>

      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 mt-6">
        <h3 className="font-head font-semibold flex items-center gap-2 mb-4"><Store className="w-4 h-4 text-volt" /> Shop items</h3>
        <div className="grid grid-cols-1 md:grid-cols-6 gap-3 items-end">
          <div className="md:col-span-2"><label className={lbl}>Name</label>
            <input data-testid="item-name" value={item.name} onChange={(e) => setItem({ ...item, name: e.target.value })} className={field} placeholder="VIP Role" /></div>
          <div><label className={lbl}>Emoji</label>
            <input data-testid="item-emoji" value={item.emoji} onChange={(e) => setItem({ ...item, emoji: e.target.value })} className={field} placeholder="⭐" maxLength={8} /></div>
          <div><label className={lbl}>Price</label>
            <input data-testid="item-price" type="number" value={item.price} onChange={(e) => setItem({ ...item, price: e.target.value })} className={field} /></div>
          <div><label className={lbl}>Type</label>
            <select data-testid="item-type" value={item.type} onChange={(e) => setItem({ ...item, type: e.target.value })} className={field}>
              <option value="item">Item</option>
              <option value="role">Role</option>
            </select></div>
          <button data-testid="item-add" onClick={addItem} className="bg-zinc-800 hover:bg-zinc-700 rounded-md px-3 py-2 flex items-center justify-center gap-1 text-sm h-[38px]"><Plus className="w-4 h-4" /> Add</button>
        </div>
        {item.type === "role" && (
          <div className="mt-3 max-w-xs"><label className={lbl}>Role name to grant</label>
            <RoleSelect guildId={guildId} value={item.role_name} onChange={(v) => setItem({ ...item, role_name: v })} testid="item-role" placeholder="— select a role —" className="mt-1 w-full" /></div>
        )}
        <div className="mt-3"><label className={lbl}>Description (optional)</label>
          <input data-testid="item-desc" value={item.description} onChange={(e) => setItem({ ...item, description: e.target.value })} className={field} placeholder="What does this item do?" /></div>
        <div className="mt-5 space-y-2">
          {shop.length === 0 && <p className="text-sm text-zinc-600">No items yet. Add roles or collectables members can buy with their currency.</p>}
          {shop.map((it) => (
            <div key={it.id} data-testid={`shop-item-${it.id}`} className="flex items-center justify-between bg-black border border-zinc-800 rounded-lg px-4 py-2.5">
              <div className="flex items-center gap-3 min-w-0">
                <span className="text-lg">{it.emoji || (it.type === "role" ? "🎭" : "📦")}</span>
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{it.name} <span className="text-xs text-zinc-600">#{it.id}</span></div>
                  <div className="text-xs text-zinc-500 truncate">{it.type === "role" ? `Grants role: ${it.role_name}` : it.description || "Item"}</div>
                </div>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <span className="text-sm text-volt font-semibold">{sym} {Number(it.price).toLocaleString()}</span>
                <button onClick={() => removeItem(it.id)} className="text-zinc-500 hover:text-red-400"><X className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ---------------- Leaderboard ---------------- */
function LeaderboardTab({ board, sortBy, switchSort, sym }) {
  const medals = ["🥇", "🥈", "🥉"];
  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <span className="text-sm text-zinc-400">{board.length} ranked</span>
        <div className="flex bg-zinc-900 border border-zinc-800 rounded-lg p-1 text-sm">
          <button data-testid="sort-xp" onClick={() => switchSort("xp")} className={`px-4 py-1.5 rounded-md font-medium transition-colors ${sortBy === "xp" ? "bg-volt text-volt-ink" : "text-zinc-400 hover:text-white"}`}>Top by XP</button>
          <button data-testid="sort-coins" onClick={() => switchSort("coins")} className={`px-4 py-1.5 rounded-md font-medium transition-colors ${sortBy === "coins" ? "bg-volt text-volt-ink" : "text-zinc-400 hover:text-white"}`}>Top by Coins</button>
        </div>
      </div>
      {board.length === 0 ? (
        <div className="text-center py-20 text-zinc-500">
          <Trophy className="w-10 h-10 mx-auto mb-3 text-zinc-700" />
          No activity yet. Once members chat and earn coins, they'll climb the ranks here.
        </div>
      ) : (
        <div className="space-y-2 max-w-3xl" data-testid="engagement-leaderboard-list">
          {board.map((m, i) => {
            const rank = i + 1;
            const cur = levelFloor(m.level);
            const next = levelFloor(m.level + 1);
            const prog = Math.max(0, Math.min(1, (m.xp - cur) / Math.max(1, next - cur)));
            return (
              <div key={m.id || i} data-testid={`leaderboard-row-${rank}`} className="flex items-center gap-4 bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 hover:border-zinc-700 transition-colors">
                <div className="w-8 text-center font-head font-black text-lg text-zinc-400">{rank <= 3 ? medals[rank - 1] : `#${rank}`}</div>
                <img src={m.avatar} alt="" className="w-11 h-11 rounded-full bg-zinc-800 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-semibold truncate">{m.username}</span>
                    <span className="text-xs text-zinc-500 flex items-center gap-1 shrink-0"><MessageSquare className="w-3 h-3" /> {(m.messages || 0).toLocaleString()}</span>
                  </div>
                  <div className="mt-1.5 h-1.5 bg-black rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-volt to-[#f59e0b]" style={{ width: `${prog * 100}%` }} />
                  </div>
                  <div className="text-[11px] text-zinc-500 mt-1">{m.xp.toLocaleString()} XP</div>
                </div>
                <div className="text-center shrink-0 w-16">
                  <div className="font-head font-black text-xl text-volt">{m.level}</div>
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500">Level</div>
                </div>
                <div className="text-center shrink-0 w-24">
                  <div className="font-head font-black text-lg text-[#facc15]">{sym} {(m.coins || 0).toLocaleString()}</div>
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500">Coins</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
